import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ExportButton from '../../components/ExportButton';

const B = '#0B3D6E', T = '#0F6E56';
const CATS = { med:'💊 Médicament', dis:'🩺 Dispositif', cons:'🧤 Consommable', lab:'🔬 Laboratoire' };
const INIT = { nameFr:'', nameEn:'', ref:'', price:'', priceNum:0, category:'med', badge:false, imageUrl:'', description:'', stockQty:999, inStock:true };

// ── Image upload component (URL + drag & drop) ─────────────────────────────
function ImageUpload({ value, onChange }) {
  const [tab, setTab] = useState(value?.startsWith('data:') ? 'upload' : 'url');
  const [drag, setDrag] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Fichier image requis (PNG, JPG, WEBP)'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop grande (max 5 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = e => { onChange(e.target.result); setTab('upload'); };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden', marginBottom:8 }}>
        {[['url','🔗 URL web'],['upload','📁 Upload fichier']].map(([t,l])=>(
          <button key={t} type="button" onClick={()=>setTab(t)} style={{ flex:1, background:tab===t?B:'#fff', color:tab===t?'#fff':'#64748b', border:'none', padding:'8px', fontSize:12, fontWeight:tab===t?600:400, cursor:'pointer', transition:'all 0.2s' }}>{l}</button>
        ))}
      </div>

      {tab === 'url' ? (
        <input type="url" placeholder="https://images.unsplash.com/photo-..."
          value={value?.startsWith('data:') ? '' : value}
          onChange={e => onChange(e.target.value)}
          style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#f8fafc', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}/>
      ) : (
        <div
          onDragOver={e=>{ e.preventDefault(); setDrag(true); }}
          onDragLeave={()=>setDrag(false)}
          onDrop={e=>{ e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={()=>document.getElementById('admin-img-input').click()}
          style={{ border:`2px dashed ${drag?B:'#e2e8f0'}`, borderRadius:8, padding:'1.5rem', textAlign:'center', cursor:'pointer', background:drag?'#E6F1FB':'#f8fafc', transition:'all 0.2s' }}>
          <input id="admin-img-input" type="file" accept="image/*" style={{ display:'none' }} onChange={e=>handleFile(e.target.files[0])}/>
          <div style={{ fontSize:28, marginBottom:6 }}>📸</div>
          <div style={{ fontSize:13, color:'#64748b' }}>Glisser-déposer ou <span style={{ color:B, fontWeight:600 }}>cliquer pour parcourir</span></div>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>PNG, JPG, WEBP — max 5 Mo</div>
        </div>
      )}

      {value && (
        <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:10 }}>
          <img src={value} alt="preview" style={{ width:72, height:54, objectFit:'cover', borderRadius:7, border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>Aperçu de l'image</div>
            <button type="button" onClick={()=>onChange('')} style={{ fontSize:11, color:'#dc2626', background:'#fee2e2', border:'none', padding:'3px 8px', borderRadius:6, cursor:'pointer' }}>× Supprimer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generate ref automatically ─────────────────────────────────────────────
function genRef(category, existingRefs) {
  const prefix = { med:'LMD-MED', dis:'LMD-DIS', cons:'LMD-CON', lab:'LMD-LAB' }[category] || 'LMD-PRD';
  let n = (existingRefs?.filter(r=>r.startsWith(prefix)).length || 0) + 1;
  return `${prefix}-${String(n).padStart(3,'0')}`;
}

export default function Products() {
  const qc = useQueryClient();
  const [view, setView]         = useState('grid');
  const [search, setSearch]     = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(INIT);
  const [tab, setTab]           = useState('info'); // modal tabs: info | image | stock

  const { data, isLoading } = useQuery({
    queryKey: ['admin-products', search, catFilter],
    queryFn: () => api.get('/products', { params:{ limit:100, search:search||undefined, cat:catFilter||undefined } }).then(r=>r.data)
  });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? api.put(`/products/${editing._id}`,d) : api.post('/products',d),
    onSuccess: () => {
      toast.success(editing ? '✅ Produit mis à jour' : '✅ Produit ajouté');
      qc.invalidateQueries(['admin-products']); qc.invalidateQueries(['products']);
      closeModal();
    },
    onError: (err) => toast.error(err.response?.data?.message||'Erreur')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => { toast.success('Produit supprimé'); qc.invalidateQueries(['admin-products']); qc.invalidateQueries(['products']); },
    onError: () => toast.error('Erreur')
  });

  const toggleBadge = useMutation({
    mutationFn: (p) => api.put(`/products/${p._id}`, { ...p, badge: !p.badge }),
    onSuccess: () => qc.invalidateQueries(['admin-products']),
  });

  const allRefs = (data?.data||[]).map(p=>p.ref);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...INIT, ref: genRef('med', allRefs) });
    setTab('info');
    setModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ nameFr:p.nameFr, nameEn:p.nameEn||'', ref:p.ref, price:p.price, priceNum:p.priceNum||0, category:p.category, badge:p.badge, imageUrl:p.imageUrl||'', description:p.description||'', stockQty:p.stockQty||999, inStock:p.inStock!==false });
    setTab('info');
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditing(null); };

  const handleCatChange = (cat) => {
    setForm(f => ({ ...f, category:cat, ref: editing ? f.ref : genRef(cat, allRefs) }));
  };

  const inp = { width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#f8fafc', color:'#1a2535', outline:'none', fontFamily:'inherit', boxSizing:'border-box' };
  const lbl = { display:'block', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:5 };

  const filtered = (data?.data||[]).filter(p => !catFilter || p.category === catFilter);

  return (
    <div style={{ padding:'1.5rem', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:600, marginBottom:2 }}>Catalogue LOMED MEDICARE</h1>
          <div style={{ fontSize:13, color:'#64748b' }}>{filtered.length} produit(s) dans le catalogue</div>
        </div>
        <button onClick={openAdd} style={{ background:B, color:'#fff', border:'none', padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ fontSize:16 }}>+</span> Ajouter un produit
        </button>
      </div>

      {/* Filters */}
      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1rem', marginBottom:'1rem', display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <svg viewBox="0 0 24 24" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', width:14, height:14, fill:'none', stroke:'#94a3b8', strokeWidth:2 }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input type="text" placeholder="Rechercher produit, référence..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ ...inp, paddingLeft:32 }}/>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {[['','Tous'],...Object.entries(CATS)].map(([k,v])=>(
            <button key={k} onClick={()=>setCatFilter(k)} style={{ background:catFilter===k?B:'#fff', color:catFilter===k?'#fff':'#64748b', border:'1px solid #e2e8f0', padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:catFilter===k?600:400, cursor:'pointer', whiteSpace:'nowrap' }}>
              {k===''?'Tous':v}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden', marginLeft:'auto' }}>
          {['grid','list'].map(v=>(
            <button key={v} onClick={()=>setView(v)} style={{ background:view===v?B:'#fff', color:view===v?'#fff':'#94a3b8', border:'none', padding:'7px 11px', cursor:'pointer', display:'flex', alignItems:'center' }}>
              {v==='grid'
                ? <svg viewBox="0 0 24 24" style={{ width:14,height:14,fill:'none',stroke:'currentColor',strokeWidth:2 }}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                : <svg viewBox="0 0 24 24" style={{ width:14,height:14,fill:'none',stroke:'currentColor',strokeWidth:2 }}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              }
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      {isLoading ? (
        <div style={{ padding:'3rem', textAlign:'center', color:'#94a3b8' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'4rem', color:'#94a3b8', background:'#fff', borderRadius:12, border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>💊</div>
          <div style={{ fontSize:14 }}>Aucun produit — <button onClick={openAdd} style={{ color:B, background:'none', border:'none', cursor:'pointer', fontWeight:600, fontSize:14 }}>Ajouter le premier</button></div>
        </div>
      ) : view==='grid' ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(185px,1fr))', gap:14 }}>
          {filtered.map(p=>(
            <div key={p._id} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', flexDirection:'column' }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=B}
              onMouseLeave={e=>e.currentTarget.style.borderColor='#e2e8f0'}>
              <div style={{ height:115, overflow:'hidden', background:'#f1f5f9', position:'relative' }}>
                {p.imageUrl
                  ? <img src={p.imageUrl} alt={p.nameFr} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>{e.target.style.display='none';}}/>
                  : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32 }}>💊</div>
                }
                <span style={{ position:'absolute', top:6, left:6, background:'#fff', border:'1px solid #e2e8f0', fontSize:9, padding:'2px 6px', borderRadius:6, fontWeight:500, color:'#64748b' }}>
                  {CATS[p.category]?.split(' ')[0]}
                </span>
                {p.badge && <span style={{ position:'absolute', top:6, right:6, background:T, color:'#fff', fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:6 }}>NEW</span>}
                {!p.inStock && <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ background:'#dc2626', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:6 }}>Rupture</span></div>}
              </div>
              <div style={{ padding:'10px 12px', flex:1, display:'flex', flexDirection:'column' }}>
                <div style={{ fontSize:9, color:'#94a3b8', fontFamily:'monospace', marginBottom:2 }}>{p.ref}</div>
                <div style={{ fontSize:12, fontWeight:600, marginBottom:'auto', lineHeight:1.3, paddingBottom:6 }}>{p.nameFr}</div>
                <div style={{ fontSize:15, fontWeight:700, color:B, marginBottom:8 }}>{p.price}</div>
                <div style={{ display:'flex', gap:5 }}>
                  <button onClick={()=>openEdit(p)} style={{ flex:2, fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'6px', borderRadius:7, cursor:'pointer', fontWeight:500 }}>✏️ Modifier</button>
                  <button onClick={()=>{ if(window.confirm(`Supprimer "${p.nameFr}" ?`)) deleteMutation.mutate(p._id); }} style={{ flex:1, fontSize:11, background:'#fee2e2', border:'1px solid #fca5a5', padding:'6px', borderRadius:7, cursor:'pointer', color:'#dc2626' }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Image','Réf.','Produit (FR/EN)','Catégorie','Prix','Badge','Stock','Actions'].map(h=>(
                    <th key={h} style={{ textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', padding:'10px 14px', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p=>(
                  <tr key={p._id} style={{ borderTop:'1px solid #f1f5f9' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 14px' }}>
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt="" style={{ width:48, height:36, objectFit:'cover', borderRadius:6, border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/>
                        : <div style={{ width:48, height:36, background:'#f1f5f9', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>💊</div>
                      }
                    </td>
                    <td style={{ padding:'8px 14px', fontFamily:'monospace', fontSize:11, color:'#94a3b8', whiteSpace:'nowrap' }}>{p.ref}</td>
                    <td style={{ padding:'8px 14px' }}>
                      <div style={{ fontSize:13, fontWeight:600 }}>{p.nameFr}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{p.nameEn}</div>
                    </td>
                    <td style={{ padding:'8px 14px' }}><span style={{ background:'#f1f5f9', padding:'3px 8px', borderRadius:6, fontSize:11 }}>{CATS[p.category]}</span></td>
                    <td style={{ padding:'8px 14px', fontWeight:700, color:B, fontSize:13, whiteSpace:'nowrap' }}>{p.price}</td>
                    <td style={{ padding:'8px 14px' }}>
                      <button onClick={()=>toggleBadge.mutate(p)} style={{ background:p.badge?T:'#f1f5f9', color:p.badge?'#fff':'#64748b', border:'none', padding:'3px 9px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        {p.badge?'✓ Nouveau':'—'}
                      </button>
                    </td>
                    <td style={{ padding:'8px 14px' }}>
                      <span style={{ background:p.inStock?'#E1F5EE':'#fee2e2', color:p.inStock?T:'#dc2626', fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8 }}>
                        {p.inStock?'En stock':'Rupture'}
                      </span>
                    </td>
                    <td style={{ padding:'8px 14px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={()=>openEdit(p)} style={{ fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'5px 10px', borderRadius:6, cursor:'pointer', whiteSpace:'nowrap' }}>✏️ Modifier</button>
                        <button onClick={()=>{ if(window.confirm(`Supprimer "${p.nameFr}" ?`)) deleteMutation.mutate(p._id); }} style={{ fontSize:11, background:'#fee2e2', border:'1px solid #fca5a5', padding:'5px 10px', borderRadius:6, cursor:'pointer', color:'#dc2626' }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal add/edit ── */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          onClick={e=>{ if(e.target===e.currentTarget) closeModal(); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:540, maxHeight:'93vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

            {/* Modal header */}
            <div style={{ padding:'1.2rem 1.5rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <h2 style={{ fontSize:16, fontWeight:600 }}>{editing?'✏️ Modifier le produit':'+ Ajouter un produit'}</h2>
                {editing && <div style={{ fontSize:11, color:'#94a3b8', marginTop:1, fontFamily:'monospace' }}>{editing.ref}</div>}
              </div>
              <button onClick={closeModal} style={{ background:'#f1f5f9', border:'none', width:28, height:28, borderRadius:7, cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'1px solid #e2e8f0', flexShrink:0 }}>
              {[['info','📋 Informations'],['image','🖼 Image'],['stock','📦 Stock & options']].map(([t,l])=>(
                <button key={t} onClick={()=>setTab(t)} style={{ flex:1, background:'#fff', color:tab===t?B:'#94a3b8', border:'none', borderBottom:`2px solid ${tab===t?B:'transparent'}`, padding:'10px 8px', fontSize:12, fontWeight:tab===t?600:400, cursor:'pointer' }}>{l}</button>
              ))}
            </div>

            {/* Modal body */}
            <div style={{ overflowY:'auto', flex:1, padding:'1.5rem' }}>

              {tab === 'info' && (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div>
                    <label style={lbl}>Nom français *</label>
                    <input style={inp} value={form.nameFr} onChange={e=>setForm({...form,nameFr:e.target.value})} placeholder="Ex: Paracétamol 500mg" required/>
                  </div>
                  <div>
                    <label style={lbl}>Nom anglais *</label>
                    <input style={inp} value={form.nameEn} onChange={e=>setForm({...form,nameEn:e.target.value})} placeholder="Ex: Paracetamol 500mg"/>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <div>
                      <label style={lbl}>Catégorie *</label>
                      <select style={inp} value={form.category} onChange={e=>handleCatChange(e.target.value)}>
                        {Object.entries(CATS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Référence *</label>
                      <input style={inp} value={form.ref} onChange={e=>setForm({...form,ref:e.target.value})} placeholder="LMD-MED-001"/>
                    </div>
                    <div>
                      <label style={lbl}>Prix (ex: 120 MAD) *</label>
                      <input style={inp} value={form.price} onChange={e=>setForm({...form,price:e.target.value, priceNum:parseFloat(e.target.value)||0})} placeholder="120 MAD"/>
                    </div>
                    <div>
                      <label style={lbl}>Badge "Nouveau"</label>
                      <select style={inp} value={String(form.badge)} onChange={e=>setForm({...form,badge:e.target.value==='true'})}>
                        <option value="false">Non</option>
                        <option value="true">Oui ✓</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Description</label>
                    <textarea style={{ ...inp, minHeight:70, resize:'vertical' }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Description optionnelle du produit..."/>
                  </div>
                </div>
              )}

              {tab === 'image' && (
                <div>
                  <label style={{ ...lbl, marginBottom:10 }}>Image du produit</label>
                  <ImageUpload value={form.imageUrl} onChange={v=>setForm({...form,imageUrl:v})}/>
                  {!form.imageUrl && (
                    <div style={{ marginTop:14, background:'#f8fafc', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#64748b', lineHeight:1.65 }}>
                      💡 <strong>Astuce :</strong> Utilisez <a href="https://unsplash.com" target="_blank" rel="noreferrer" style={{ color:B }}>Unsplash.com</a> pour trouver des photos médicales gratuites. Cliquez sur une photo → clic droit → Copier l'adresse de l'image → coller dans URL web.
                    </div>
                  )}
                </div>
              )}

              {tab === 'stock' && (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div>
                    <label style={lbl}>Disponibilité</label>
                    <div style={{ display:'flex', gap:10 }}>
                      {[['true','✅ En stock'],['false','❌ Rupture de stock']].map(([v,l])=>(
                        <button key={v} type="button" onClick={()=>setForm({...form,inStock:v==='true'})} style={{ flex:1, background:String(form.inStock)===v?B:'#f8fafc', color:String(form.inStock)===v?'#fff':'#64748b', border:`1px solid ${String(form.inStock)===v?B:'#e2e8f0'}`, padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Quantité en stock</label>
                    <input type="number" style={inp} min="0" value={form.stockQty} onChange={e=>setForm({...form,stockQty:Number(e.target.value)})}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Quantité affichée dans le catalogue (999 = illimité)</div>
                  </div>
                  <div style={{ background:'#E6F1FB', border:'1px solid #B5D4F4', borderRadius:10, padding:'12px' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:B, marginBottom:6 }}>📋 Récapitulatif du produit</div>
                    <div style={{ fontSize:12, color:'#475569', lineHeight:1.8 }}>
                      <strong>Nom :</strong> {form.nameFr||'—'}<br/>
                      <strong>Référence :</strong> {form.ref||'—'}<br/>
                      <strong>Prix :</strong> {form.price||'—'}<br/>
                      <strong>Catégorie :</strong> {CATS[form.category]}<br/>
                      <strong>Badge :</strong> {form.badge?'✅ Nouveau':'Non'}<br/>
                      <strong>Image :</strong> {form.imageUrl?'✅ Configurée':'❌ Manquante'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding:'1rem 1.5rem', borderTop:'1px solid #f1f5f9', display:'flex', gap:10, justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ display:'flex', gap:6 }}>
                {['info','image','stock'].map((t,i)=>(
                  <div key={t} style={{ width:8, height:8, borderRadius:'50%', background:tab===t?B:'#e2e8f0', cursor:'pointer', transition:'background 0.2s' }} onClick={()=>setTab(t)}/>
                ))}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={closeModal} style={{ background:'#f1f5f9', color:'#64748b', border:'1px solid #e2e8f0', padding:'9px 18px', borderRadius:8, fontSize:13, cursor:'pointer' }}>Annuler</button>
                <button onClick={()=>saveMutation.mutate(form)} disabled={saveMutation.isPending||!form.nameFr||!form.ref||!form.price}
                  style={{ background:saveMutation.isPending||!form.nameFr?'#94a3b8':B, color:'#fff', border:'none', padding:'9px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', opacity:(!form.nameFr||!form.ref||!form.price)?0.6:1 }}>
                  {saveMutation.isPending?'⏳ Enregistrement...':(editing?'Mettre à jour':'Ajouter au catalogue')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

